import { useTranslation } from 'react-i18next';
import Sidebar from '@/components/sidebar';
import { PageHeaderLayout } from '@/components/layouts/PageHeaderLayout';
import { LogoLoader } from '@/components/ui/logo-loader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useNotifications,
  useUnreadNotificationsCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  type NotificationItem,
} from '@/hooks/use-notifications';
import { Bell, Check, CheckCheck, Trash2, FileText, Users, Calendar, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { trackEvent } from '@/lib/analytics';
import { ANALYTICS_EVENTS } from '@mangelapp/shared';
import { normalizeTicketTimestampInput } from '@/utils/ticket-document-time';

function formatTimeAgo(dateString: string, t: (key: string, options?: any) => string): string {
  const date = normalizeTicketTimestampInput(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMs < 0 || diffMinutes < 1) return t('time.justNow');
  if (diffMinutes < 60) return t('time.minutesAgo', { count: diffMinutes });
  if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
  return t('time.daysAgo', { count: diffDays });
}

function getCategoryIcon(category: string) {
  switch (category) {
    case 'ticket':
      return <FileText className="w-5 h-5" />;
    case 'project':
      return <Calendar className="w-5 h-5" />;
    case 'organization':
      return <Users className="w-5 h-5" />;
    case 'subscription':
      return <CreditCard className="w-5 h-5" />;
    default:
      return <Bell className="w-5 h-5" />;
  }
}

function NotificationCard({
  notification,
  onMarkRead,
  onDelete,
  t,
}: {
  notification: NotificationItem;
  onMarkRead: () => void;
  onDelete: () => void;
  t: (key: string, options?: any) => string;
}) {
  const [, setLocation] = useLocation();

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkRead();
    }
    if (notification.actionUrl) {
      // Normalize URL: fix various legacy URL patterns
      let url = notification.actionUrl;

      // Convert /projects/ to /project/ for consistent routing
      if (url.startsWith('/projects/')) {
        url = url.replace('/projects/', '/project/');
      }

      // Fix tab=plans to tab=planLevel (legacy notifications)
      url = url.replace('tab=plans', 'tab=planLevel');

      // Fix legacy member routes to contractors page
      if (
        url === '/team' ||
        url.startsWith('/team?') ||
        url.startsWith('/settings/team') ||
        url.startsWith('/settings?tab=team') ||
        url.startsWith('/settings?tab=members')
      ) {
        url = '/contractors';
      }

      // Fix legacy documents route:
      // - project-scoped notifications should open project documents tab
      // - organization-scoped notifications should open listen page
      if (
        url === '/settings/documents' ||
        url.startsWith('/settings/documents?') ||
        url === '/settings?tab=documents' ||
        url.startsWith('/settings?tab=documents&')
      ) {
        url = notification.projectId ? `/project/${notification.projectId}?tab=documents` : '/listen';
      }

      setLocation(url);
    }
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-colors hover:bg-gray-50',
        !notification.isRead && 'bg-blue-50/50 border-l-4 border-l-blue-500'
      )}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex-shrink-0 p-2 rounded-full',
              notification.isRead ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-600'
            )}
          >
            {getCategoryIcon(notification.category)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4
                className={cn(
                  'text-sm truncate',
                  notification.isRead ? 'font-normal text-gray-700' : 'font-semibold text-gray-900'
                )}
              >
                {notification.title}
              </h4>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {formatTimeAgo(notification.createdAt, t)}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {!notification.isRead && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onMarkRead}
                title={t('actions.markRead')}
              >
                <Check className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={onDelete}
              title={t('actions.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function NotificationsPage() {
  const { t } = useTranslation('pages.notifications');
  const { data: notifications, isLoading } = useNotifications();
  const { data: unreadCount } = useUnreadNotificationsCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.NOTIFICATION_OPENED);
  }, []);

  const notificationsList = notifications || [];

  const actionButtons = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => markAllRead.mutate()}
      disabled={markAllRead.isPending || !unreadCount || unreadCount === 0}
    >
      <CheckCheck className="w-4 h-4 mr-2" />
      {t('actions.markAllRead')}
    </Button>
  );

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 ml-[3.25rem] sm:ml-[3.75rem] md:ml-[4.25rem] lg:ml-[3.75rem] xl:ml-[4.25rem] 2xl:ml-[5.25rem]">
        <PageHeaderLayout
          pageTitle={t('title')}
          pageSubtitle={t('subtitle')}
          actionButtons={actionButtons}
        >
          <div className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LogoLoader size="md" />
              </div>
            ) : notificationsList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Bell className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">{t('emptyState.title')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('emptyState.description')}</p>
              </div>
            ) : (
              <div className="space-y-3 max-w-3xl">
                {notificationsList.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkRead={() => markRead.mutate(notification.id)}
                    onDelete={() => deleteNotification.mutate(notification.id)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>
        </PageHeaderLayout>
      </div>
    </div>
  );
}
